import { Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Chart, ChartConfiguration, ChartType } from 'chart.js';
import Annotation from 'chartjs-plugin-annotation';
import { BaseChartDirective } from 'ng2-charts';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, interval, lastValueFrom, of, Subscription } from 'rxjs';
import { AvailableResources, NetworkUsage, NetworkUsageData, NodeUsage } from 'src/app/core/models/charts';
import { CustomData } from 'src/app/core/models/customData';
import { Deployment } from 'src/app/core/models/deployment';
import { DashboardModulesE, Graphs } from 'src/app/core/models/globals';
import { QuickAccessControl } from 'src/app/core/models/quickAccessControl';
import { SessionsByDeployments } from 'src/app/core/models/session';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { ChartsService } from 'src/app/core/services/charts.service';
import { DeploymentService } from 'src/app/core/services/deployment.service';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { HeaderService } from 'src/app/core/services/header.service';
import { JobsService } from 'src/app/core/services/jobs.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { NodeService } from 'src/app/core/services/node.service';
import { SessionService } from 'src/app/core/services/session.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { SyncConfigService } from 'src/app/core/services/sync-config.service';
import { UserService } from 'src/app/core/services/user.service';
import DatalabelsPlugin from "chartjs-plugin-datalabels";
import { formatDate } from '@angular/common';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { EditColoumsComponent } from 'src/app/modules/node/modals/edit-coloums/edit-coloums.component';
import { UserSetting } from 'src/app/core/models/userSetting';
import { ShowAllDeploymentSessionsComponent } from 'src/app/modules/deployment/modals/show-all-deployment-sessions/show-all-deployment-sessions.component';
import { UserRoleNames } from 'src/app/core/models/users';

interface DashboardI {
  name: string;
  title: DashboardModulesE;
  subTitle: string;
  icon: string;
  data: string[];
  isDataClickable: boolean;
  labels: string[];
  threshold: any[];
  tooltip: string[];
  color: string;
}
export interface ChartConfig {
  name: string;
  title: string;
  isVisible: boolean;
  isDefault: boolean; // Charts that should always be visible by default
}
interface LicenseUsageI {
  type: string;
  inUse: number;
  total: number;
  usedPercent: number;
}

interface TimeCountData {
  time: string;
  count: number;
}

interface JobSessionData {
  jobInfo: TimeCountData[];
  sessionInfo: TimeCountData[];
}


@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrl: './analytics.component.scss',
})
export class AnalyticsComponent implements OnInit {

  constructor(
    private env: EnvironmentService,
    private sanitizer: DomSanitizer,
    private navService : NavigationService,
    private chartService: ChartsService,
    private router: Router,
    private syncConfig: SyncConfigService,
    private spinner: NgxSpinnerService,
    private snackBar: MatSnackBar,
    private nodeService: NodeService,
    private depService: DeploymentService,
    private sessionService: SessionService,
    private authenticationService: AuthenticationService,
    public dialog: MatDialog,
    private userService: UserService,
    public storageService: StorageService,
    private http: HttpClient,
    private headerService: HeaderService,
    private jobsService: JobsService,
  ){
    Chart.register(Annotation);
  }

  showDeploymentDashboard: boolean = true;
  
  quickAccessControls: QuickAccessControl = { deployments: null, licenseServerNames: null };
  lastRefreshedTime: Date; // time when data is loaded/refreshed

  dashboardUrl: SafeResourceUrl;
  envGrafanaDashboardUrl: string = this.env.envVariables.grafanaDashboardUrl;

  // #region [Dashboard Data]
  @ViewChild("nodeUtilizationChart", { static: false })
  nodeUtilizationCanvas: BaseChartDirective;
  @ViewChild("jobSessionsChart", { static: true })
  jobSessionsCanvas: BaseChartDirective;
  @ViewChildren(BaseChartDirective) charts: QueryList<BaseChartDirective>;
  dateFilterValue = "2";
  granularityValue = "One Hour";
  nodeUtilization: NodeUsage[];
  networkUsage: NetworkUsage[];
  availableResources: AvailableResources[];
  dashboardData;
  networkGaugethresholdsFromEnvironment: string[] =
    this.env.envVariables.networkGaugethresholds?.split(",");
  gaugeType = "arch";
  cpuGaugeLabel = "CPU";
  gaugeAppendText = "%";
  networkAppendText = "Mbps";
  public gaugeThick = 15;
  public cpuGaugeValue = 0;
  public gaugeThresholds = {
    "0": { color: "green" },
    "70": { color: "orange" },
    "90": { color: "red" },
  };
  public networkGaugethresholds = {};
  public gaugemax = 100;
  networkGaugeMax = 60000;
  gaugeSize = 200;
  public ramGaugeValue = 0;
  public gpuMemoryGaugeValue = 0;
  public networkGaugeValue = 100;
  ramGaugeLabel = "RAM";
  gpuMemoryGaugeLabel = "GPU Memory";
  networkGaugeLabel = "Network (Total)";
  dataReloadSubscription: Subscription; // subscription for reloading/ refreshing data every 10seconds
  dashboard: DashboardI[];
  graphHeight: number = 530;
  isGrafanaConfigured: boolean = this.env.envVariables.isGrafanaConfigured || false;
  activeTab: string = 'Grafana';

  //#region [node utilization chart variables]
  nodeCpuUtilizationData: ChartConfiguration["data"] = {
    datasets: [
      {
        data: [],
        label: "CPU",
        backgroundColor: "lightgreen",
        borderColor: "lightgreen",
        pointBackgroundColor: "lightgreen",
        pointBorderColor: "lightgreen",
        pointHoverBackgroundColor: "rgba(255, 161, 181,0.3)",
        pointHoverBorderColor: "red",
        fill: "origin",
      },
    ],
    labels: [],
  };
  nodeRamUtilizationData: ChartConfiguration["data"] = {
    datasets: [
      {
        data: [],
        label: "RAM",
        backgroundColor: "lightgreen",
        borderColor: "lightgreen",
        pointBackgroundColor: "lightgreen",
        pointBorderColor: "lightgreen",
        pointHoverBackgroundColor: "rgba(255, 161, 181,0.3)",
        pointHoverBorderColor: "red",
        fill: "origin",
      },
    ],
    labels: [],
  };
  nodeGpuUtilizationData: ChartConfiguration["data"] = {
    datasets: [
      {
        data: [],
        label: "GPU",
        backgroundColor: "lightgreen",
        borderColor: "lightgreen",
        pointBackgroundColor: "lightgreen",
        pointBorderColor: "lightgreen",
        pointHoverBackgroundColor: "rgba(255, 161, 181,0.3)",
        pointHoverBorderColor: "red",
        fill: "origin",
      },
    ],
    labels: [],
  };
  nodeUtilizationData: ChartConfiguration["data"] = {
    datasets: [
      {
        data: [],
        label: "CPU",
        backgroundColor: "#11009E",
        borderColor: "#11009E",
        borderWidth: 1, // Adjust the width of the line
        borderJoinStyle: "miter", // Set to 'miter' for sharp lines
        pointRadius: 0,
        pointHoverRadius: 0,
        hidden: false,
      },

      {
        data: [],
        label: "RAM",
        backgroundColor: "#FE7A36",
        borderColor: "#FE7A36",
        borderWidth: 1, // Adjust the width of the line
        borderJoinStyle: "miter", // Set to 'miter' for sharp lines
        pointRadius: 0,
        pointHoverRadius: 0,
        hidden: false,
      },
      {
        data: [],
        label: "GPU",
        backgroundColor: "#527853",
        borderColor: "#527853",
        borderWidth: 1, // Adjust the width of the line
        borderJoinStyle: "miter", // Set to 'miter' for sharp lines
        pointRadius: 0,
        pointHoverRadius: 0,
        hidden: false,
      },
    ],
  };
  jobSessionsData: ChartConfiguration["data"] = {
    datasets: [
      {
        data: [],
        label: "Jobs",
        backgroundColor: "#ED7D31",
        borderColor: "#ED7D31",
        hoverBackgroundColor: "#ED7D31",
        hoverBorderColor: "#ED7D31",
        borderWidth: 2, // Adjust the width of the line
        // borderJoinStyle: 'miter', // Set to 'miter' for sharp lines
        pointRadius: 0,
        pointHoverRadius: 0,
        hidden: false,
      },
      {
        data: [],
        label: "Sessions",
        backgroundColor: "#3876BF",
        borderColor: "#3876BF",
        hoverBackgroundColor: "#3876BF",
        hoverBorderColor: "#3876BF",
        borderWidth: 2, // Adjust the width of the line
        // borderJoinStyle: 'miter', // Set to 'miter' for sharp lines
        pointRadius: 0,
        pointHoverRadius: 0,
        hidden: false,
      },
    ],
  };
  networkData: ChartConfiguration["data"] = {
    datasets: [
      {
        data: [],
        label: "Send",
        backgroundColor: "#ED7D31",
        borderColor: "#ED7D31",
        hoverBackgroundColor: "#ED7D31",
        hoverBorderColor: "#ED7D31",
        pointHoverBackgroundColor: "rgba(255, 161, 181,0.3)",
        pointHoverBorderColor: "red",
        fill: false,
        order: 1,
      },
      {
        data: [],
        label: "Receive",
        backgroundColor: "#40A2D8",
        borderColor: "#40A2D8",
        hoverBackgroundColor: "#40A2D8",
        hoverBorderColor: "#40A2D8",
        pointHoverBackgroundColor: "rgba(255,225,153,0.3)",
        pointHoverBorderColor: "red",
        fill: false,
        order: 1,
      },
      {
        data: [],
        label: "Send Avg.",
        backgroundColor: "#99BC85",
        borderColor: "#99BC85",
        hoverBackgroundColor: "#99BC85",
        hoverBorderColor: "#99BC85",
        pointHoverBackgroundColor: "rgba(255,225,153,0.3)",
        pointHoverBorderColor: "red",
        fill: false,
        type: "line",
        order: 0,
      },
      {
        data: [],
        label: "Receive Avg.",
        backgroundColor: "#B06161",
        borderColor: "#B06161",
        hoverBackgroundColor: "#B06161",
        hoverBorderColor: "#B06161",
        pointHoverBackgroundColor: "rgba(255,225,153,0.3)",
        pointHoverBorderColor: "red",
        fill: false,
        type: "line",
        order: 0,
      },
    ],
    labels: [],
  };
  hpcUnitsConsumedDataTotal: ChartConfiguration["data"] = {
    datasets: [
      {
        data: [],
        label: "HPC Units",
        backgroundColor: "rgba(255, 161, 181,0.2)",
        borderColor: "red",
        pointBackgroundColor: "rgba(77,83,96,1)",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "rgba(255, 161, 181,0.2)",
        pointHoverBorderColor: "#fff",
        fill: "origin",
      },
    ],
    labels: [],
  };
  licenseUsageData: ChartConfiguration["data"] = {
    datasets: [
      {
        data: [],
        label: "",
        backgroundColor: "#6962AD",
        hoverBackgroundColor: "#6C22A6",
      },
    ],
    labels: [],
  };

  nodeCpuUtilizationOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0.5,
      },
      point: {
        radius: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      "y-axis-0": {
        position: "left",
        grid: {
          display: false,
        },
        max: 100,
        min: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "CPU utilization of last 1 Hour",
        position: "bottom",
      },
      datalabels: {
        display: false,
      },
    },
  };
  nodeRamUtilizationOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0.5,
      },
      point: {
        radius: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      "y-axis-0": {
        position: "left",
        grid: {
          display: false,
        },
        max: 100,
        min: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "RAM utilization of last 1 Hour",
        position: "bottom",
      },
      datalabels: {
        display: false,
      },
    },
  };
  nodeGpuUtilizationOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0.5,
      },
      point: {
        radius: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      "y-axis-0": {
        position: "left",
        grid: {
          display: false,
        },
        max: 100,
        min: 0,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: "GPU utilization of last 1 Hour",
        position: "bottom",
      },
      datalabels: {
        display: false,
      },
    },
  };
  nodeUtilizationOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
        },
        title: {
          display: true,
          text: "Time",
          color: "black",
          font: {
            size: 12,
          },
        },
      },
      "y-axis-0": {
        position: "left",
        grid: {
          display: true,
        },
        max: 100,
        suggestedMax: 100,
        min: 0,
        ticks: {
          stepSize: 10,
        },
        title: {
          display: true,
          text: "Usage in %",
          color: "black",
          font: {
            size: 12,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
      title: {
        display: false,
        text: "Node utilization of last 1 Hour",
        position: "top",
        font: {
          size: 14,
        },
      },
      datalabels: {
        anchor: "end",
        align: "end",
        display: false,
      },
    },
  };
  jobSessionsOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0.4,
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
        },
        title: {
          display: true,
          text: "Time",
          color: "black",
          font: {
            size: 12,
          },
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Jobs & Sessions Count",
          color: "black",
          font: {
            size: 12,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
      title: {
        display: true,
        text: "Job and Sessions Count - Last 7 Days",
        position: "top",
        font: {
          size: 14,
        },
      },
      datalabels: {
        anchor: "end",
        align: "end",
        display: false,
      },
    },
  };
  hpcUnitsConsumedOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0,
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(255,0,0,0.1)",
          borderColor: "black",
        },
        title: {
          display: true,
          text: "Time",
          color: "black",
          font: {
            size: 12,
          },
        },
      },
      "y-axis-0": {
        position: "left",
        grid: {
          color: "rgba(255,0,0,0.1)",
          borderColor: "black",
        },
        min: 0,
        title: {
          display: true,
          text: "HPC Units Used",
          color: "black",
          font: {
            size: 12,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          boxWidth: 20,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: `HPC Units Consumed - Last 7 Days`,
        position: "top",
      },
      datalabels: {
        // backgroundColor:(context) => {
        //   return context.dataset.backgroundColor;
        // },
        backgroundColor: "red",
        borderRadius: 4,
        color: "white",
        font: {
          weight: "bold",
        },
        formatter: Math.round,
        padding: 6,
        display: (context) => {
          return +context.dataset.data[context.dataIndex] > 0;
        },
      },
    },
  };
  networkOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    elements: {
      line: {
        tension: 0,
      },
      point: {
        radius: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
        },
        title: {
          display: true,
          text: "Time",
          color: "black",
          font: {
            size: 12,
          },
        },
      },
      "y-axis-0": {
        position: "left",
        grid: {
          display: true,
        },
        title: {
          display: true,
          text: "Usage in MB",
          color: "black",
          font: {
            size: 12,
          },
        },
        min: 0,
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 20,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: "Network utilization of last 1 Hour",
        position: "top",
      },
      datalabels: {
        color: "black",
        font: {
          weight: "bold",
        },
        formatter: Math.round,
        padding: 6,
        // display: (context) => {
        //   return +context.dataset.data[context.dataIndex] > 0;
        // },
        display: false,
      },
    },
  };
  licenseUsageOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: "right",
        labels: {
          boxWidth: 15,
        },
      },
      title: {
        display: true,
        text: `License Usage`,
        position: "top",
      },
      datalabels: {
        anchor: "center",
        align: "center",
        display: (context) => {
          return +context.dataset.data[context.dataIndex] > 0;
        },
        formatter: (value, context) => {
          // Custom logic to format the data label
          return value + " %"; // You can add any custom value here
        },
        color: "white",
      },
    },
    scales: {
      x: {
        grid: {
          color: "rgba(255,0,0,0.1)",
          borderColor: "black",
        },
        title: {
          display: true,
          text: "Applications",
          color: "black",
          font: {
            size: 12,
          },
        },
      },
      "y-axis-0": {
        position: "left",
        grid: {
          color: "rgba(255,0,0,0.1)",
          borderColor: "black",
        },
        max: 100,
        title: {
          display: true,
          text: "Usage In Percentage",
          color: "black",
          font: {
            size: 12,
          },
        },
      },
    },
  };
  availableCharts: ChartConfig[] = [
    {
      name: 'nodeUtilization',
      title: 'Node Utilization of Last 1 Hour',
      isVisible: true,
      isDefault: true // Always show by default
    },
    {
      name: 'jobSessions',
      title: 'Job and Sessions Count - Last 7 Days',
      isVisible: true,
      isDefault: true // Always show by default
    },
    {
      name: 'networkUsage',
      title: 'Network Usage',
      isVisible: false,
      isDefault: false
    },
    {
      name: 'licenseUsage',
      title: 'License Usage',
      isVisible: false,
      isDefault: false
    },
    {
      name: 'hpcUnits',
      title: 'HPC Units Consumed',
      isVisible: true,
      isDefault: true
    }
  ];
  nodeUtilizationType: ChartType = "line";
  networkType: ChartType = "bar";
  hpcUnitsConsumedType: ChartType = "line";
  licenseUsageType: ChartType = "bar";
  nodeUtilizationPlugins = [DatalabelsPlugin];
  hpcUnitsConsumedPlugins = [DatalabelsPlugin];
  networkPlugins = [DatalabelsPlugin];
  licenseUsage = [DatalabelsPlugin];
  //#endregion [node utilization chart variables]

  currentUserRole = this.authenticationService.getRole();
  sessionsByDep: SessionsByDeployments[];
  currentLoggedInUser = this.authenticationService.currentUserValue.username;
  columnInfo: {
    columnsOfComponents: string[];
    coloumToShow: string[];
  };
  x: boolean = true;
  columnList: string[] = ["First Name", "Last Name"];
  editedColumnList: string[] = [];
  depList: string[] = [];
  thirdDep: boolean = false;
  cloudProvidersList: string[] = [];
  deployment: Deployment;
  tooltipHtml: string;
  allTiles;
  customDataArray: CustomData[] = [];
  
  // #endregion [Dashboard Data End]

  showDeploymentGraph = true;
  isDeploymentAdmin: boolean = this.currentUserRole == UserRoleNames.DeploymentAdmin;
  deployments: string[];
  showLinkToTrustGrafanaURL: boolean = false;
  
    async ngOnInit() {
      const params = "orgId=1&kiosk&theme=light";
      this.dashboardUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `${this.envGrafanaDashboardUrl}`
      );


      this.navService.sendLastUpdate(this.headerService.getUserNewDate());
      this.navService.lastUpdateTime.subscribe((x) => {
        this.lastRefreshedTime = x;
      });

      
      this.quickAccessControls = await lastValueFrom(this.headerService.getQuickAccessControlSummary());
      this.deployments = this.quickAccessControls.deployments.map((d) => d.name);

      this.dashboardLoad();

      this.checkGrafanaUrl();
      
    }


  editChartsToShow() {
    const editChartDialog = this.dialog.open(EditColoumsComponent, {
      autoFocus: false,
      disableClose: true,
      width: "450px",
      data: {
        columnsFromComponents: this.availableCharts.map(chart => chart.title),
        previoslySelectedColumns: this.availableCharts
          .filter(chart => chart.isVisible)
          .map(chart => chart.title)
      },
    });

    editChartDialog.afterClosed().subscribe((res) => {
      if (res.status) {
        console.log(res);
        if (res.data != null && res.data.length >= 0) {
          // Extract only the checked items and get their names
          const selectedTitles = res.data
            .filter((item: any) => item.checked === true)
            .map((item: any) => item.name);

          console.log('Selected titles:', selectedTitles);

          // Convert titles back to chart names for updateChartSettings
          const selectedNames = selectedTitles.map((title: string) => {
            const chart = this.availableCharts.find(c => c.title === title);
            return chart ? chart.name : title;
          });

          console.log('Selected chart names:', selectedNames);
          this.updateChartSettings(selectedNames);
        }
      }
    });
  }
  // Method to update chart visibility settings
  updateChartSettings(selectedCharts: string[]) {
    this.availableCharts.forEach(chart => {
      if (chart.isDefault) {
        // For default charts, check if they're in the selected list
        // If user unchecked them in the dialog, respect that choice
        chart.isVisible = selectedCharts.includes(chart.name);
      } else {
        // For non-default charts, set visibility based on selection
        chart.isVisible = selectedCharts.includes(chart.name);
      }
    });

    // Save settings to localStorage
    this.saveChartSettings();
  }

  // Method to check if a chart should be visible
  isChartVisible(chartName: string): boolean {
    const chart = this.availableCharts.find(c => c.name === chartName);
    return chart ? chart.isVisible : false;
  }

  // Save chart settings
  saveChartSettings() {
    const settings = this.availableCharts.map(chart => ({
      name: chart.name,
      isVisible: chart.isVisible
    }));
    localStorage.setItem('chartSettings', JSON.stringify(settings));
  }

  // Load chart settings
  loadChartSettings() {
    const savedSettings = localStorage.getItem('chartSettings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      settings.forEach((setting: any) => {
        const chart = this.availableCharts.find(c => c.name === setting.name);
        if (chart && !chart.isDefault) { // Don't override default charts
          chart.isVisible = setting.isVisible;
        }
      });
    }
  }
    checkGrafanaUrl(){
      this.spinner.show("analytics");
      fetch(this.envGrafanaDashboardUrl, { mode: 'no-cors' })
      .then(() => {
        this.showLinkToTrustGrafanaURL = false;
        this.spinner.hide("analytics");
      })
      .catch((err) => {
        this.showLinkToTrustGrafanaURL = true;
        console.error("User's browser blocked the request:", err);
        this.spinner.hide("analytics");
      });
    }

    ngOnDestroy() {
      this.dataReloadSubscription?.unsubscribe();
    }


    reloadDeploymentGraphComponent() {
      this.showDeploymentGraph = false;
      setTimeout(() => this.showDeploymentGraph = true, 0);
    }

    reloadDeploymentDashboardComponent() {
      this.showDeploymentDashboard = false;
      setTimeout(() => {
        this.showDeploymentDashboard = true;

        setTimeout(() => {
          const reportsTab = document.getElementById('Reports-tab');
          const dashboardTab = document.getElementById('Dashboad-tab');
          if(reportsTab) {
            reportsTab.click();
          }
          if (dashboardTab) {
            dashboardTab.click();
          }
        }, 100);

        this.dashboardLoad();
      }, 50);

    }

    reloadGrafanaDashboard(){
      this.checkGrafanaUrl();
      this.dashboardUrl = this.sanitizer.bypassSecurityTrustResourceUrl('about:blank');
      setTimeout(() => {
        this.dashboardUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.envGrafanaDashboardUrl);
      }, 10);
    }

    // #region [Dashboard Data Functions]

    dashboardLoad(){ 
      // #region [Dashboard Data ngOnInit]
            this.spinner.show("tiles");
            this.spinner.show("usage");
            this.spinner.show("network");
            this.spinner.show("session");
            this.spinner.show("hpcUnits");
            this.spinner.show("license");
            this.columnInfo = {
              columnsOfComponents: [],
              coloumToShow: [
                DashboardModulesE.Session,
                DashboardModulesE.Nodes,
                DashboardModulesE.Users,
                DashboardModulesE.Deployment,
              ],
            };
            this.columnInfo.columnsOfComponents = [
              DashboardModulesE.Session,
              DashboardModulesE.SessionsByOS,
              DashboardModulesE.Nodes,
              DashboardModulesE.Users,
              // DashboardModulesE.Deployment,
              DashboardModulesE.Jobs,
              DashboardModulesE.License,
              DashboardModulesE.NodesThreshold,
              DashboardModulesE.Storage,
              DashboardModulesE.SessionsByDeployments,
              DashboardModulesE.AvailableResources,
            ];
            this.editedColumnList = [
              DashboardModulesE.Session,
              DashboardModulesE.Nodes,
              DashboardModulesE.Users,
            ];
            this.networkGaugethresholds = {
              "0": { color: "green" },
              [this.networkGaugethresholdsFromEnvironment[0]]: { color: "orange" },
              [this.networkGaugethresholdsFromEnvironment[1]]: { color: "red" },
            };
            this.networkGaugeMax = Number(
              this.networkGaugethresholdsFromEnvironment[2]
            );
            this.getColumnSetting();
            this.navService.sendLastUpdate(this.headerService.getUserNewDate());
            this.navService.lastUpdateTime.subscribe((x) => {
              this.lastRefreshedTime = x;
            });
            this.dataReloadSubscription = interval(
              this.syncConfig.config.dashboard.listReqInterval
            ).subscribe((x) => {
              this.getCustomData();
              this.getDashboardData();
              this.getAverageUsageForDashboard();
              this.loadNodeUtilization();
              this.loadNetworkUtilization();
              this.loadJobSessionChart();
              this.loadLicenseUsage();
              this.navService.sendLastUpdate(this.headerService.getUserNewDate());
            });
      // #endregion [Dashboard Data ngOnInit End]

    }


  async getDashboardData() {
    this.chartService.getDashboardData(this.currentLoggedInUser).subscribe({
      next: (data) => {
        let dashboard = JSON.parse(JSON.stringify(data));
        this.dashboardData?.forEach((d) => {
          if (d.name == DashboardModulesE.Session) {
            d.data = [dashboard.activeSessions];
            d.data.push(dashboard.disconnectedSessions);
            d.data.push(dashboard.inactiveSessions);
          }
          if (d.name == DashboardModulesE.Cores) {
            d.data = [dashboard.totalCore];
            d.data.push(dashboard.inUseCore);
            d.data.push(dashboard.idleCore);
          } else if (d.name == DashboardModulesE.Nodes) {
            d.data = [dashboard.totalNodes];
            d.data.push(dashboard.assignedNodes);
            d.data.push(dashboard.unassignedNodes);
          } else if (d.name == DashboardModulesE.NodesThreshold) {
            d.data = [dashboard.cpuThresholdHitCount];
            d.data.push(dashboard.ramThresholdHitCount);
            d.data.push(dashboard.gpuMemoryThresholdHitCount);
          } else if (d.name == DashboardModulesE.Deployment) {
            //d.data = [dashboard.totalDeployment];
            this.depList = dashboard.totalDeployment?.split(",");
            if (this.depList.length > 3) {
              this.thirdDep = true;
              d.data = [this.depList[0]];
              d.data.push(this.depList[1]);
              d.data.push("...");
              d.tooltip.push("");
              d.tooltip.push("");
              d.tooltip.push("Show All Deployments");
              d.labels = ["Total Deployments : " + this.depList.length];
            } else {
              d.data = this.depList;
              d.labels = ["Total Deployments : " + this.depList.length];
            }
          } else if (d.name == DashboardModulesE.Jobs) {
            d.data = [dashboard.totalJobs];
            d.data.push(dashboard.activeJobs);
            d.data.push(dashboard.completedJobs);
          } else if (d.name == DashboardModulesE.Users) {
            d.data = [dashboard.totalUsers];
            d.data.push(dashboard.jobRunningUsers);
            d.data.push(dashboard.sessionUsers);
          } else if (d.name == DashboardModulesE.SessionsByOS) {
            d.data = [dashboard.windowsSessions];
            d.data.push(dashboard.linuxSessions);
            d.data.push(dashboard.otherSessions);
          } else if (d.name == DashboardModulesE.SessionsByDeployments) {
            if (dashboard?.sessionByDeployments) {
              this.sessionsByDep = <SessionsByDeployments[]>(
                JSON.parse(JSON.stringify(dashboard.sessionByDeployments))
              );
              if (this.sessionsByDep.length > 3) {
                d.labels = [this.sessionsByDep[0].deployment];
                d.data = [this.sessionsByDep[0].totalSessions];
                d.labels.push(this.sessionsByDep[1].deployment);
                d.data.push(this.sessionsByDep[1].totalSessions);
                d.labels.push("Expand All");
                d.data.push("...");
                d.tooltip.push(
                  "Total active sessions of " + this.sessionsByDep[0].deployment
                );
                d.tooltip.push(
                  "Total active sessions of " + this.sessionsByDep[1].deployment
                );
                d.tooltip.push("Show Active Sessions Of All Deployments");
              } else {
                let i = 0;
                dashboard.sessionByDeployments.forEach((dep) => {
                  if (i == 0) {
                    d.labels = [dep.deployment];
                    d.data = [dep.totalSessions];
                    d.tooltip = ["Total active sessions of " + dep.deployment];
                    i += 1;
                  } else {
                    d.labels.push(dep.deployment);
                    d.data.push(dep.totalSessions);
                    d.tooltip.push(
                      "Total active sessions of " + dep.deployment
                    );
                  }
                });
              }
            } else {
              d.data = ["NA", "NA", "NA"];
              d.labels = [];
            }
          } else if (d.name == DashboardModulesE.AvailableResources) {
            if (dashboard?.availableResources) {
              this.availableResources = <AvailableResources[]>(
                JSON.parse(JSON.stringify(dashboard.availableResources))
              );
              if (this.availableResources.length > 0) {
                const cpuSum = this.availableResources.reduce(
                  (accumulator, currentObject) => {
                    return accumulator + currentObject.availableCpu;
                  },
                  0
                );
                d.data = [cpuSum];
                const ramSum = this.availableResources.reduce(
                  (accumulator, currentObject) => {
                    return accumulator + currentObject.availableRam;
                  },
                  0
                );
                d.data.push(ramSum + " GB");
                const gpuSum = this.availableResources.reduce(
                  (accumulator, currentObject) => {
                    return accumulator + currentObject.availableGpu;
                  },
                  0
                );
                d.data.push(gpuSum + " GB");
              } else {
                d.data = ["NA", "NA", "NA"];
                d.labels = ["Unable to find data."];
              }
            } else {
              d.data = ["NA", "NA", "NA"];
              d.labels = [];
            }
          } else if (d.name == DashboardModulesE.Storage) {
            d.data = [dashboard.totalStorage];
            d.data.push(dashboard.usedStorage);
            d.data.push(dashboard.remainingStorage);
          } else if (d.name == DashboardModulesE.License) {
            d.data = [dashboard.totalLicenses];
            d.data.push(dashboard.inUseLicenses);
            d.data.push(dashboard.idleLicenses);
          }
        });
        this.spinner.hide("tiles");
      },
      error: (err) => {
        this.spinner.hide("tiles");
      },
    });
  }

    loadJobSessionChart() {
      this.chartService.getJobSessionsForPeriod("7Days").subscribe({
        next: (data) => {
          if (data) {
            let graphData: JobSessionData = JSON.parse(JSON.stringify(data));
  
            graphData.jobInfo.forEach((info, index) => {
              let time = formatDate(
                new Date(info.time),
                "shortDate",
                "en-US"
              ).split(",")[0];
              this.jobSessionsData.labels[index] = time;
              this.jobSessionsData.datasets[0].data[index] = info.count;
              this.jobSessionsData.datasets[1].data[index] =
                graphData.sessionInfo[index].count;
            });
  
            if (graphData.jobInfo.length > 0) {
              this.jobSessionsData.datasets[0].data.length =
                graphData.jobInfo.length;
              this.jobSessionsData.datasets[1].data.length =
                graphData.jobInfo.length;
            }
  
            if (this.jobSessionsData.datasets[0].data.every((x) => x === 0))
              this.jobSessionsData.datasets[0].hidden = true;
            if (this.jobSessionsData.datasets[1].data.every((x) => x === 0))
              this.jobSessionsData.datasets[1].hidden = true;
            this.jobSessionsCanvas?.update();
          }
          this.spinner.hide("session");
        },
        error: (err) => {
          this.spinner.hide("session");
          this.snackBar.open("Unable to find job sessions report.", "OK", {
            duration: 5000,
          });
        },
      });
    }
  
    navigateToComponent(value) {
      const ReportsTabButton = document.getElementById("Reports-tab") as HTMLElement;
      switch (value.toLowerCase()) {
        case "deployments":
          this.router.navigate(["/Deployment List"]);
          break;
  
        case "nodes":
          this.router.navigate(["/Nodes List/User/All/Node"]);
          break;
  
        case "nodes threshold":
          this.router.navigate(["/Nodes List/User/All/Node"]);
          break;
  
        case "jobs":
          this.router.navigate(["/Job List"]);
          break;
  
        case "sessions":
          this.router.navigate(["/Sessions List"]);
          break;
  
        case "users":
          this.router.navigate(["/User List"]);
          break;
  
        case "storages":
          this.router.navigate(["/Storage List"]);
          break;
  
        case "licenses":
          this.router.navigate(["/Licenses List"]);
          break;
  
        case "cpu":
          this.depService.dashboardReportRedirect = value;
          this.depService.dashboardReportRedirectDep = "all";
          ReportsTabButton?.click();
          break;
  
        case "ram":
          this.depService.dashboardReportRedirect = value;
          this.depService.dashboardReportRedirectDep = "all";
          ReportsTabButton?.click();
          break;
  
        case "gpu":
          this.depService.dashboardReportRedirect = value;
          this.depService.dashboardReportRedirectDep = "all";
          ReportsTabButton?.click();
          break;
  
        case "network":
          this.depService.dashboardReportRedirect = value;
          this.depService.dashboardReportRedirectDep = "all";
          ReportsTabButton?.click();
          break;
  
        case "sessions by deployments":
          this.depService.dashboardReportRedirect = "sessionbydep";
          this.depService.dashboardReportRedirectDep = "all";
          ReportsTabButton?.click();
          break;
  
        case "sessions by os":
          this.router.navigate(["/Sessions List"]);
          break;
  
        default:
          break;
      }
    }
  
    getAverageUsageForDashboard() {
      this.chartService.getCpuUtilizationAverage().subscribe({
        next: (data) => {
          if (data) {
            let res = JSON.parse(JSON.stringify(data));
            this.cpuGaugeValue =
              res.cpuUtilizationAverage > 0
                ? Number(
                    (Math.round((res.cpuUtilizationAverage * 10) ^ 2) / 10) ^ 2
                  )
                : res.cpuUtilizationAverage;
            this.ramGaugeValue =
              res.ramMemoryUtilization > 0
                ? Number(
                    (Math.round((res.ramMemoryUtilization * 10) ^ 2) / 10) ^ 2
                  )
                : res.ramMemoryUtilization;
            this.gpuMemoryGaugeValue =
              res.gpuMemoryUtilization > 0
                ? Number(
                    (Math.round((res.gpuMemoryUtilization * 10) ^ 2) / 10) ^ 2
                  )
                : res.gpuMemoryUtilization;
            this.networkGaugeValue = res.networkUtilization;
            if (res.networkUsage) {
              this.networkUsage = JSON.parse(JSON.stringify(res.networkUsage));
            }
          }
        },
        error: (err) => {
          this.cpuGaugeValue = 0;
          this.ramGaugeValue = 0;
          this.gpuMemoryGaugeValue = 0;
          this.networkGaugeValue = 0;
        },
      });
    }
  
    loadNodeUtilization() {
      this.chartService
        .getNodeUsage(
          "all",
          Graphs.NodeUtilization,
          this.dateFilterValue,
          this.granularityValue,
          "",
          "",
          false,
          ""
        )
        .subscribe({
          next: (data) => {
            if (data) {
              this.nodeUtilization = <NodeUsage[]>(
                JSON.parse(JSON.stringify(data))
              );
              this.nodeUtilization = this.nodeUtilization;
              if (this.nodeUtilization) {
                //const uniqueDates = [...new Set(this.nodeUtilization.map(item => item.timeStamp))];
                let uniqueDates;
                if (!["1", "2"].includes(this.dateFilterValue)) {
                  uniqueDates = [
                    ...new Set(
                      this.nodeUtilization.map(
                        (item) =>
                          formatDate(
                            new Date(item.timeStamp),
                            "mediumDate",
                            "en-US"
                          ).split(",")[0]
                      )
                    ),
                  ];
                } else {
                  uniqueDates = [
                    ...new Set(
                      this.nodeUtilization.map((item) => item.timeStamp)
                    ),
                  ];
                }
                uniqueDates.forEach((unq, idx) => {
                  let count = 0;
                  let ramUsage = 0;
                  let cpuUsage = 0;
                  let gpuUsage = 0;
                  let gpuMemoryUsage = 0;
                  let bytesSent = 0;
                  let bytesReceived = 0;
                  this.nodeUtilization.forEach((n) => {
                    if (!["1", "2"].includes(this.dateFilterValue)) {
                      if (
                        formatDate(
                          new Date(n.timeStamp),
                          "mediumDate",
                          "en-US"
                        ).split(",")[0] === unq
                      ) {
                        ramUsage = ramUsage + n.ramPercent;
                        cpuUsage = cpuUsage + n.cpuPercent;
                        gpuUsage = gpuUsage + n.gpuPercent;
                        gpuMemoryUsage = gpuMemoryUsage + n.gpuMemoryUsage;
                        bytesReceived = bytesReceived + n.bytesReceived;
                        bytesSent = bytesSent + n.bytesSent;
                        count = count + 1;
                      }
                    } else {
                      if (n.timeStamp === unq) {
                        ramUsage = ramUsage + n.ramPercent;
                        cpuUsage = cpuUsage + n.cpuPercent;
                        gpuUsage = gpuUsage + n.gpuPercent;
                        gpuMemoryUsage = gpuMemoryUsage + n.gpuMemoryUsage;
                        bytesReceived = bytesReceived + n.bytesReceived;
                        bytesSent = bytesSent + n.bytesSent;
                        count = count + 1;
                      }
                    }
                  });
                  if (this.nodeCpuUtilizationData.datasets[0].data[idx]) {
                    this.nodeCpuUtilizationData.datasets[0].data[idx] =
                      cpuUsage / count;
                  } else {
                    this.nodeCpuUtilizationData.datasets[0].data.push(
                      cpuUsage / count
                    );
                  }
                  if (this.nodeRamUtilizationData.datasets[0].data[idx]) {
                    this.nodeRamUtilizationData.datasets[0].data[idx] =
                      ramUsage / count;
                  } else {
                    this.nodeRamUtilizationData.datasets[0].data.push(
                      ramUsage / count
                    );
                  }
                  if (this.nodeGpuUtilizationData.datasets[0].data[idx]) {
                    this.nodeGpuUtilizationData.datasets[0].data[idx] =
                      gpuMemoryUsage / count;
                  } else {
                    this.nodeGpuUtilizationData.datasets[0].data.push(
                      gpuMemoryUsage / count
                    );
                  }
                  if (this.nodeUtilizationData.datasets[0].data[idx]) {
                    this.nodeUtilizationData.datasets[0].data[idx] =
                      cpuUsage / count;
                  } else {
                    this.nodeUtilizationData.datasets[0].data.push(
                      cpuUsage / count
                    );
                  }
                  if (this.nodeUtilizationData.datasets[1].data[idx]) {
                    this.nodeUtilizationData.datasets[1].data[idx] =
                      ramUsage / count;
                  } else {
                    this.nodeUtilizationData.datasets[1].data.push(
                      ramUsage / count
                    );
                  }
                  if (this.nodeUtilizationData.datasets[2].data[idx]) {
                    this.nodeUtilizationData.datasets[2].data[idx] =
                      gpuUsage / count;
                  } else {
                    this.nodeUtilizationData.datasets[2].data.push(
                      gpuUsage / count
                    );
                  }
  
                  let date;
                  if (["1", "2"].includes(this.dateFilterValue)) {
                    date = formatDate(new Date(unq), "h:mm aa", "en-US").split(
                      ")"
                    )[0];
                  } else {
                    date = formatDate(new Date(unq), "mediumDate", "en-US").split(
                      ","
                    )[0];
                  }
                  if (!this.nodeCpuUtilizationData.labels.includes(date))
                    this.nodeCpuUtilizationData.labels.push(date);
                  if (!this.nodeRamUtilizationData.labels.includes(date))
                    this.nodeRamUtilizationData.labels.push(date);
                  if (!this.nodeGpuUtilizationData.labels.includes(date))
                    this.nodeGpuUtilizationData.labels.push(date);
                  if (!this.nodeUtilizationData.labels.includes(date))
                    this.nodeUtilizationData.labels.push(date);
                });
                
                //this.nodeUtilizationChart.update();
                try {
                  this.charts.forEach((child) => {
                    child?.chart?.update();
                  });
                } catch (error) {
                  console.error(error);
                }
                this.nodeUtilizationCanvas?.chart?.update();
                this.spinner.hide("usage");
                this.spinner.hide("network");
              } else {
                this.snackBar.open("No node utilization report found.", "OK", {
                  duration: 5000,
                });
              }
            }
          },
          error: (err) => {
            this.spinner.hide("usage");
            this.spinner.hide("network");
            this.snackBar.open("Unable to find node utilization report.", "OK", {
              duration: 5000,
            });
          },
        });
    }
  
    loadHpcUnitsData() {
      this.chartService
        .getHpcUnitsConsumed(
          "all",
          "HpcUnitsConsumed",
          "7Days",
          this.granularityValue
        )
        .subscribe({
          next: (data: any) => {
            if (data) {
              this.hpcUnitsConsumedDataTotal.datasets[0].data = [];
              this.hpcUnitsConsumedDataTotal.labels = [];
              let dateFilter = "7days";
              if (
                this.dateFilterValue.indexOf("hr") != -1 ||
                this.dateFilterValue.indexOf("days") != -1
              ) {
                dateFilter =
                  this.dateFilterValue.indexOf("hr") != -1
                    ? this.dateFilterValue.trim().slice(0, -2).trim()
                    : this.dateFilterValue.trim().slice(0, -4).trim();
              }
  
              let hpcUnitsConsumedData: any;
  
              hpcUnitsConsumedData = JSON.parse(JSON.stringify(data));
              hpcUnitsConsumedData = hpcUnitsConsumedData?.sort(
                (a, b) =>
                  0 -
                  (b.deploymentName.toLowerCase() < a.deploymentName.toLowerCase()
                    ? 1
                    : -1)
              );
  
              let uniqueDates;
              if (
                dateFilter != "1" &&
                this.dateFilterValue.indexOf("days") != -1
              ) {
                uniqueDates = [
                  ...new Set(
                    hpcUnitsConsumedData.map(
                      (item) =>
                        formatDate(
                          new Date(item.entryTime),
                          "mediumDate",
                          "en-US"
                        ).split(",")[0]
                    )
                  ),
                ];
              } else {
                uniqueDates = [
                  ...new Set(hpcUnitsConsumedData.map((item) => item.entryTime)),
                ];
              }
  
              if (uniqueDates) {
                uniqueDates = uniqueDates?.sort(
                  (a, b) => new Date(a).getTime() - new Date(b).getTime()
                );
              }
  
              var filteredData = this.groupByNodes(
                hpcUnitsConsumedData,
                "entryTime"
              );
              uniqueDates.forEach((unq) => {
                let date;
                date = formatDate(new Date(unq), "shortDate", "en-US").split(
                  ","
                )[0];
                this.hpcUnitsConsumedDataTotal.labels.push(date);
              });
  
              const sortedKeys = Object.keys(filteredData)?.sort();
  
              sortedKeys?.forEach((key, idx) => {
                let units: any = 0;
                units = filteredData[key].reduce(
                  (accumulator, currentValue) =>
                    accumulator + currentValue.hpcUnitsUsed,
                  0
                );
  
                this.hpcUnitsConsumedDataTotal.datasets[0].data.push(units);
              });
              this.spinner.hide("hpcUnits");
            } else {
              this.spinner.hide("hpcUnits");
            }
          },
          error: (err) => {
            this.spinner.hide("hpcUnits");
          },
        });
    }
  
    async loadLicenseUsage() {
      const licenseData: LicenseUsageI[] = await lastValueFrom(
        this.chartService.getLicenseUsage()
      );
      this.licenseUsageData.labels = licenseData?.map((x) => x.type);
      this.licenseUsageData.datasets[0].data = licenseData?.map((x) =>
        Number(x.usedPercent.toFixed(1))
      );
      this.spinner.hide("license");
    }
  
    groupByNodes(obj, key) {
      return obj.reduce(function (start, current) {
        if (current[key].split(",").length > 1) {
          current[key].split(",").forEach((eleKey) => {
            (start[current[eleKey]] = start[current[eleKey]] || []).push({
              ...current,
              jobAllocatedNodes: eleKey,
            });
            return start;
          });
        } else {
          (start[current[key]] = start[current[key]] || []).push(current);
        }
        return start;
      }, {});
    }
  
    refreshComponent() {
      let url = this.router.url;
      this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
        this.router.navigate([decodeURI(url)]);
      });
    }
  
    onDataClick(dashboard: DashboardI, index: number): void {
      if (dashboard.isDataClickable) {
        switch (dashboard.title) {
          case DashboardModulesE.NodesThreshold:
            this.nodeService.dashboardRedirectSort = dashboard.labels[index];
            this.router.navigate(["/Nodes List/User/All/Node"]);
            break;
  
          default:
            break;
        }
      }
  
      let clickedItem = dashboard.title + " " + dashboard.subTitle;
  
      switch (clickedItem.toLowerCase().trim()) {
        case "sessions":
          switch (index) {
            case -1:
              this.router.navigate(["/Sessions List"]);
              break;
  
            case 0:
              if (Number(dashboard.data[0]) === 0) {
                this.sessionService.dashboardSessionRedirectSort = null;
              } else {
                this.sessionService.dashboardSessionRedirectSort =
                  dashboard.labels[index];
              }
              this.router.navigate(["/Sessions List"]);
              break;
  
            case 1:
              if (Number(dashboard.data[1]) === 0) {
                this.sessionService.dashboardSessionRedirectSort = null;
              } else {
                this.sessionService.dashboardSessionRedirectSort =
                  dashboard.labels[index];
              }
              this.router.navigate(["/Sessions List"]);
              break;
  
            case 2:
              if (Number(dashboard.data[2]) === 0) {
                this.sessionService.dashboardSessionRedirectSort = null;
              } else {
                this.sessionService.dashboardSessionRedirectSort =
                  dashboard.labels[index];
              }
              this.router.navigate(["/Sessions List"]);
              break;
          }
          break;
  
        case "nodes crossing threshold":
          this.router.navigate(["/Nodes List"]);
          break;
  
        case "nodes":
          this.navService.sendChildData(null);
          this.navService.sendCloud(null);
          if (this.currentUserRole == UserRoleNames.GlobalAdmin) {
            switch (index) {
              case -1:
                this.router.navigate(["/Nodes List"]);
                break;
  
              case 0:
                if (Number(dashboard.data[0]) === 0) {
                  this.nodeService.dashboardRedirectSort = null;
                } else {
                  this.nodeService.dashboardRedirectSort =
                    dashboard.labels[index];
                }
                this.router.navigate(["/Nodes List/User/All/Node"]);
                break;
  
              case 1:
                if (Number(dashboard.data[1]) === 0) {
                  this.nodeService.dashboardRedirectSort = null;
                } else {
                  this.nodeService.dashboardRedirectSort =
                    dashboard.labels[index];
                }
                this.router.navigate(["/Nodes List/User/All/Node"]);
                break;
  
              case 2:
                if (Number(dashboard.data[2]) === 0) {
                  this.nodeService.dashboardRedirectSort = null;
                } else {
                  this.nodeService.dashboardRedirectSort =
                    dashboard.labels[index];
                }
                this.router.navigate(["/Nodes List/User/All/Node"]);
                break;
  
              default:
                this.router.navigate(["/Nodes List"]);
                break;
            }
          } else {
            this.router.navigate(["/Nodes List"]);
            break;
          }
          break;
  
        case "users":
          switch (index) {
            case -1:
              this.router.navigate(["/User List"]);
              break;
  
            case 0:
              this.router.navigate(["/User List"]);
              break;
  
            case 1:
              this.router.navigate(["/Job List"]);
              break;
  
            case 2:
              this.router.navigate(["/Sessions List"]);
              break;
          }
          break;
  
        case "deployments":
          if (dashboard.data[index] == "...") {
            this.router.navigate(["/Deployment List"]);
            break;
          } else {
            this.depService.getDeployment(dashboard.data[index]).subscribe({
              next: (data: any) => {
                if (data) {
                  this.deployment = <Deployment>JSON.parse(JSON.stringify(data));
                  this.router.navigate([
                    "Deployments List",
                    this.deployment.cloud,
                    this.deployment.name,
                  ]);
                } else {
                  this.router.navigate(["/Deployment List"]);
                }
              },
              error: (err) => {
                this.router.navigate(["/Deployment List"]);
              },
            });
            break;
          }
  
        case "jobs":
          if(index != -1 && index != 0) {
            this.jobsService.dashboardFilterApplied = dashboard.labels[index];
          }
          this.router.navigate(["/Job List"]);
          break;
  
        case "storages":
        case "storages in gb":
          this.router.navigate(["/Storage List"]);
          break;
  
        case "licenses":
          this.router.navigate(["/Licenses List"]);
          break;
  
        case "sessions by os":
          switch (index) {
            case 0:
              if (Number(dashboard.data[0]) === 0) {
                this.sessionService.dashboardSessionRedirectSort = null;
              } else {
                this.sessionService.dashboardSessionRedirectSort =
                  dashboard.labels[index];
              }
              this.router.navigate(["/Sessions List"]);
              break;
  
            case 1:
              if (Number(dashboard.data[1]) === 0) {
                this.sessionService.dashboardSessionRedirectSort = null;
              } else {
                this.sessionService.dashboardSessionRedirectSort =
                  dashboard.labels[index];
              }
              this.router.navigate(["/Sessions List"]);
              break;
  
            case 2:
              if (Number(dashboard.data[2]) === 0) {
                this.sessionService.dashboardSessionRedirectSort = null;
              } else {
                this.sessionService.dashboardSessionRedirectSort =
                  dashboard.labels[index];
              }
              this.router.navigate(["/Sessions List"]);
              break;
          }
          break;
  
        case "sessions by deployments":
          if (dashboard.data[index] == "...") {
            const dialogRef = this.dialog.open(
              ShowAllDeploymentSessionsComponent,
              {
                width: "55%",
                data: {
                  dashboardCardValue: this.sessionsByDep,
                  cardVariable: {
                    "icon": "bi bi-window-stack",
                    "title": "Sessions By Deployments",
                    "data": [],
                    "isDataClickable": false,
                    "labels": [],
                    "threshold": [],
                    "tooltip": [],
                    "cursor": "pointer",
                  },
                  cardType: null,
                  cardLabelValue: null,
                },
              }
            );
            break;
          } else {
            if (Number(dashboard.data[index]) === 0) {
              this.sessionService.dashboardSessionRedirectSort = null;
            } else {
              this.sessionService.dashboardSessionRedirectSort =
                "deployment," + dashboard.labels[index];
            }
            this.router.navigate(["/Sessions List"]);
            break;
          }
  
        case "available resources":
          const dialogRef = this.dialog.open(ShowAllDeploymentSessionsComponent, {
            width: "55%",
            data: {
              dashboardCardValue: this.availableResources,
              cardVariable: {
                "title": DashboardModulesE.AvailableResources,
                "icon": "bi bi-collection",
                "data": [],
                "isDataClickable": false,
                "labels": ["Physical Cores", "RAM", "GPU"],
                "threshold": [],
                "tooltip": [],
                "cursor": "default",
              },
              cardType: null,
              cardLabelValue: dashboard.labels[index],
            },
          });
          break;
      }
    }
  
    getColumnSetting() {
      this.userService
        .getColumnSetting(this.currentLoggedInUser)
        .pipe(catchError((err) => of(null)))
        .subscribe(async (res) => {
          if (res) {
            let s = JSON.parse(JSON.stringify(res));
            if (s.data != null) {
              let colInfo: UserSetting = <UserSetting>(
                JSON.parse(JSON.stringify(s.data))
              );
              colInfo.columnSetting.forEach((e) => {
                if (e.tableName == "dashboard") {
                  this.editedColumnList = e.columnInfo.split(",");
                  this.columnInfo = {
                    ...this.columnInfo,
                    coloumToShow: [...this.editedColumnList],
                  };
                }
              });
              this.allTiles = await lastValueFrom(
                this.http.get<any[]>("assets/config/dashboardTiles.json")
              );

              this.dashboardData = this.allTiles.filter((d) =>
                this.editedColumnList.includes(d.name)
              );
              this.getDashboardData();
              this.getAverageUsageForDashboard();
              this.loadNodeUtilization();
              this.loadNetworkUtilization();
              this.loadJobSessionChart();
              this.loadHpcUnitsData();
              this.loadLicenseUsage();
              this.getCustomData();
            }
          }
        });
    }
  
    editColoum() {
      const editColumnDailog = this.dialog.open(EditColoumsComponent, {
        autoFocus: false,
        disableClose: true,
        width: "400px",
        data: {
          columnsFromComponents: [...this.columnInfo.columnsOfComponents],
          previoslySelectedColumns: [...this.columnInfo.coloumToShow],
        },
      });
      editColumnDailog.afterClosed().subscribe((res) => {
        if (res.status) {
          if (res.data != null && res.data.length >= 0) {
            this.updateColumnSetting(res);
          }
        }
      });
    }
  
    updateColumnSetting(res: any) {
      let columsName = res.data.map((el) => el.name);
      let columsNameString = columsName.join(",");
      this.userService
        .updateUserColumnSetting(
          this.currentLoggedInUser,
          columsNameString,
          "dashboard"
        )
        .subscribe((data) => {
          if (data) {
            let response = JSON.parse(JSON.stringify(data));
            if (response.status == 200) {
              this.editedColumnList = columsNameString.split(",");
              this.columnInfo = {
                ...this.columnInfo,
                coloumToShow: [...this.editedColumnList],
              };
              this.dashboardData = this.allTiles.filter((d) =>
                this.editedColumnList.includes(d.name)
              );
              this.snackBar.open(response.actionMessage, "Ok", {
                duration: 5000,
              });
            } else if (response.status != 200) {
              this.snackBar.open(response.actionMessage, "Ok", {
                duration: 5000,
              });
            }
          }
        });
    }
  
    getTooltipForNetwork() {
      this.tooltipHtml = null;
      if (this.networkUsage && this.networkUsage?.length != 0) {
        this.networkUsage?.forEach((nu) => {
          if (this.tooltipHtml) {
            this.tooltipHtml =
              this.tooltipHtml +
              `<p>Name : ${nu.name} ; Received : ${
                nu.receivedBytes > 0
                  ? nu.receivedBytes.toFixed(4)
                  : nu.receivedBytes
              } Mbps ; Sent : ${
                nu.transmittedBytes > 0
                  ? nu.transmittedBytes.toFixed(4)
                  : nu.transmittedBytes
              } Mbps </p><hr>`;
          } else {
            this.tooltipHtml = `<p>Name : ${nu.name} ; Received : ${
              nu.receivedBytes > 0
                ? nu.receivedBytes.toFixed(4)
                : nu.receivedBytes
            } Mbps ; Sent : ${
              nu.transmittedBytes > 0
                ? nu.transmittedBytes.toFixed(4)
                : nu.transmittedBytes
            } Mbps </p><hr>`;
          }
        });
      } else {
        this.tooltipHtml = `<p>Network utilization of all active nodes<p>`;
      }
  
      return this.tooltipHtml;
    }
  
    getLabels(labels: string[]) {
      return labels.filter((l, i) => l != " " && i != 0);
    }
  
    drop(event: CdkDragDrop<string[]>) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  
    // Event handler for CPU gauge hover
    onCpuGaugeHover(): void {
      this.toggleDatasetVisibility(0); // Index of the CPU dataset
    }
  
    // Event handler for RAM gauge hover
    onRamGaugeHover(): void {
      this.toggleDatasetVisibility(1); // Index of the RAM dataset
    }
  
    // Event handler for GPU gauge hover
    onGpuGaugeHover(): void {
      this.toggleDatasetVisibility(2); // Index of the GPU dataset
    }
  
    // Toggle the visibility of the specified dataset
    toggleDatasetVisibility(datasetIndex: number): void {
      for (let index = 0; index < 3; index++) {
        if (datasetIndex != index) {
          this.nodeUtilizationCanvas.data.datasets[index].hidden =
            !this.nodeUtilizationCanvas.data.datasets[index].hidden;
          this.nodeUtilizationCanvas.data.datasets[index].borderWidth = 1;
        } else {
          let borderWidth =
            this.nodeUtilizationCanvas.data.datasets[index].borderWidth;
          this.nodeUtilizationCanvas.data.datasets[index].borderWidth =
            borderWidth == 1 ? 3 : 1;
        }
      }
  
      // Update the chart
      this.nodeUtilizationCanvas.update();
    }
  
    removeTile(tile: DashboardI) {
      let columns = this.columnInfo.coloumToShow.filter((c) => c != tile.name);
      this.updateColumnSetting({
        status: true,
        data: columns.map((c) => ({ name: c, checked: true })),
      });
    }
  
    //!custom data component methods are here
    checkLengthCustomArray(i: number): string {
      if (this.customDataArray.length === 1) {
        return "col-12";
      } else if (this.customDataArray.length - 1 === i && i % 2 === 0) {
        return "col-12";
      } else return "col-md-6";
    }
  
    getCustomData() {
      this.depService.getCustomData().subscribe({
        next: (data) => {
          if (data) {
            if (Array.isArray(data)) {
              this.customDataArray = JSON.parse(JSON.stringify(data));
              this.customDataArray = this.customDataArray.sort((a, b) => {
                return a.header.length - b.header.length;
              });
            } else this.customDataArray = [JSON.parse(JSON.stringify(data))];
          }
        },
        error: (error) => {
          this.snackBar.open("Unable to find Custom Data.", "OK", {
            duration: 5000,
          });
        },
      });
    }
    //! end of custom data component
  
    loadNetworkUtilization() {
      this.chartService
        .getNetworkUsage(
          "all",
          Graphs.NodeUtilization,
          this.dateFilterValue,
          this.granularityValue,
          "",
          "",
          false,
          ""
        )
        .subscribe({
          next: (data) => {
            if (data) {
              let networkUtilization = <NetworkUsageData[]>(
                JSON.parse(JSON.stringify(data))
              );
              if (networkUtilization) {
                //const uniqueDates = [...new Set(networkUtilization.map(item => item.timeStamp))];
                let uniqueDates;
                if (!["1", "2"].includes(this.dateFilterValue)) {
                  uniqueDates = [
                    ...new Set(
                      networkUtilization.map(
                        (item) =>
                          formatDate(
                            new Date(item.timeStamp),
                            "mediumDate",
                            "en-US"
                          ).split(",")[0]
                      )
                    ),
                  ];
                } else {
                  uniqueDates = [
                    ...new Set(
                      networkUtilization.map((item) => item.timeStamp)
                    ),
                  ];
                }
                this.networkData.labels = [];
                uniqueDates.forEach((unq, idx) => {
                  let count = 0;
                  let bytesSent = 0;
                  let bytesReceived = 0;
                  networkUtilization.forEach((n) => {
                    if (!["1", "2"].includes(this.dateFilterValue)) {
                      if (
                        formatDate(
                          new Date(n.timeStamp),
                          "mediumDate",
                          "en-US"
                        ).split(",")[0] === unq
                      ) {
                        bytesReceived = bytesReceived + n.bytesReceived;
                        bytesSent = bytesSent + n.bytesSent;
                        count = count + 1;
                      }
                    } else {
                      if (n.timeStamp === unq) {
                        bytesReceived = bytesReceived + n.bytesReceived;
                        bytesSent = bytesSent + n.bytesSent;
                        count = count + 1;
                      }
                    }
                  });
                  if (this.networkData.datasets[0].data[idx]) {
                    this.networkData.datasets[0].data[idx] = bytesSent;
                  } else {
                    this.networkData.datasets[0].data.push(bytesSent);
                  }
                  if (this.networkData.datasets[1].data[idx]) {
                    this.networkData.datasets[1].data[idx] = bytesReceived;
                  } else {
                    this.networkData.datasets[1].data.push(bytesReceived);
                  }
  
                  let date;
                  if (["1", "2"].includes(this.dateFilterValue)) {
                    date = formatDate(new Date(unq), "h:mm aa", "en-US").split(
                      ")"
                    )[0];
                  } else {
                    date = formatDate(new Date(unq), "mediumDate", "en-US").split(
                      ","
                    )[0];
                  }
                  this.networkData.labels.push(date);
                });
  
                if (this.networkData.datasets[0].data.length > 0) {
                }
                this.networkData.datasets[2].data = [];
                this.networkData.datasets[3].data = [];
                this.networkData.datasets[2].data.length = uniqueDates.length;
                this.networkData.datasets[3].data.length = uniqueDates.length;
  
                // get average of this.networkData.datasets[0].data and this.networkData.datasets[1].data
                let data: any[] = this.networkData.datasets[0].data;
                let avgSend =
                  data.reduce((a, b) => a + b, 0) /
                  this.networkData.datasets[0].data.length;
                data = this.networkData.datasets[1].data;
                let avgReceive =
                  data.reduce((a, b) => a + b, 0) /
                  this.networkData.datasets[1].data.length;
  
                this.networkData.datasets[2].data.fill(avgSend);
                this.networkData.datasets[3].data.fill(avgReceive);
  
                try {
                  this.charts.forEach((child) => {
                    child?.chart?.update();
                  });
                } catch (error) {
                  console.error(error);
                }
                this.spinner.hide("usage");
                this.spinner.hide("network");
              } else {
                this.snackBar.open("No node utilization report found.", "OK", {
                  duration: 5000,
                });
              }
            }
          },
          error: (err) => {
            this.snackBar.open("Unable to find node utilization report.", "OK", {
              duration: 5000,
            });
          },
        });
    }

    // #endregion [Dashboard Data Functions End]
}
